import React, { useEffect, useRef, useState } from 'react';
import './resource-manager.styles.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxArchive,
  faEye,
  faEyeSlash,
  faPen,
  faPlus,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { getCollectionFromFirestore } from '@/utils/getDataFromDbSimplified';
import { UseCloudAdminFn } from '@/utils/firebase.utils';
import { ModalAlert } from '@/components/modal/modal.component';
import { properCase } from '@/utils/properCase';
import SearchBar from '@/components/SHARED/search-bar/search-bar.component';
import { format } from 'date-fns';

const RESOURCE_TYPES = [
  { value: 'video', label: 'Video' },
  { value: 'blog', label: 'Blog Post' },
  { value: 'reel', label: 'Reel' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'article', label: 'Article' },
  { value: 'tool', label: 'Tool' },
  { value: 'thread', label: 'Thread' },
  { value: 'other', label: 'Other' },
];

const defaultForm = {
  title: '',
  type: '',
  url: '',
  description: '',
  tags: [],
};

const ResourceManager = () => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const [resources, setResources] = useState([]);
  const [formData, setFormData] = useState({ ...defaultForm });
  const [resourceToEdit, setResourceToEdit] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [existingTags, setExistingTags] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(10);
  const dataRef = useRef({ ...defaultForm });
  const resourcesRef = useRef();
  let resourcesListener;

  useEffect(() => {

    return () => {
      if (resourcesListener) resourcesListener();
    }
  }, []);

  useEffect(() => {
    if (!userProfile) return;
    getResources();
  }, [userProfile])

  function getResources() {
    getCollectionFromFirestore({
      'path': 'resources',
      'callback': callback,
      'handleListener': handleListener
    })
    function callback(data) {
      resourcesRef.current = {
        ...resourcesRef.current,
        ...data
      }
      setResources(Object.values(resourcesRef.current));
      const tagsSet = new Set(existingTags);
      Object.values(resourcesRef.current).forEach(r => {
        r.tags?.forEach(t => tagsSet.add(t));
      })
      setExistingTags(Array.from(tagsSet));
    }
    function handleListener(listener) {
      resourcesListener = listener;
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    dataRef.current = { ...dataRef.current, [name]: value };
    setFormData({ ...dataRef.current });
  }

  function handleTagInputChange(e) {
    const val = e.target.value;
    setTagInput(val);
    if (val.trim()) {
      const filtered = existingTags.filter(
        (t) =>
          t.toLowerCase().includes(val.toLowerCase()) &&
          !formData.tags.includes(t)
      );
      setTagSuggestions(filtered);
    } else {
      setTagSuggestions([]);
    }
  }

  function addTag(tag) {
    const trimmed = tag.trim();
    if (!trimmed || !formData?.tags || formData?.tags.includes(trimmed)) return;
    const newTags = [...formData?.tags, trimmed] || [];
    dataRef.current = { ...dataRef.current, tags: newTags };
    setFormData({ ...dataRef.current });
    setTagInput('');
    setTagSuggestions([]);
  }

  function handleTagKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(tagInput);
    }
  }

  function removeTag(tag) {
    const newTags = formData?.tags.filter((t) => t !== tag) || [];
    dataRef.current = { ...dataRef.current, tags: newTags };
    setFormData({ ...dataRef.current });
  }

  function loadForEdit(resource) {
    dataRef.current = { ...resource };
    setFormData({ ...resource });
    setResourceToEdit(resource.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleCancel() {
    dataRef.current = { ...defaultForm };
    setFormData({ ...defaultForm });
    setResourceToEdit(null);
    setTagInput('');
    setTagSuggestions([]);
  }

  async function hideOrArchiveResource(resource, action) {
    const updated = { ...resource };
    if (action === 'hide') {
      updated.status = 'hidden';
    } else if (action === 'archive') {
      updated.status = 'archived';
    } else if (action === 'reinstate') {
      updated.status = 'active';
    }
    const res = await UseCloudAdminFn(
      'saveResource',
      {
        resourceData: updated
      }
    )
    console.log(res);
    if (res.error) {
      setAlertMessage(`Something went wrong: ${res.error}`);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    const tempData = { ...formData };
    if (!tempData.title || !tempData.type || !tempData.url) {
      alert('Please fill in all required fields (title, type, url) before saving.');
      return;
    }
    if (!tempData.appId) {
      tempData.appId = userProfile.appId;
      tempData.status = 'active';
      tempData.visitCount = 0;
    }
    const res = await UseCloudAdminFn(
      'saveResource',
      {
        resourceData: tempData,
      }
    )
    console.log('saveResource response: ', res);
    if (res.error) {
      setAlertMessage(`Something went wrong: ${res.error}`);
      return;
    }
    handleCancel();
  }

  return (
    <div className='resource-manager-container'>
      <div className='g-card'>
        <div className='section-title'>
          {resourceToEdit ? 'Edit Resource' : 'Create New Resource'}
        </div>
        <hr />
        <form onSubmit={handleSave}>
          <div className='field'>
            <label htmlFor='res-title'>Title</label>
            <input
              id='res-title'
              type='text'
              name='title'
              value={formData.title || ''}
              onChange={handleChange}
              placeholder='Resource title'
              required
            />
          </div>
          <div className='g-space-1'></div>
          <div className='field'>
            <label htmlFor='res-type'>Type</label>
            <select
              id='res-type'
              name='type'
              value={formData.type || ''}
              onChange={handleChange}
              required
            >
              <option value=''>Select a type...</option>
              {RESOURCE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className='g-space-1'></div>
          <div className='field'>
            <label htmlFor='res-url'>URL</label>
            <input
              id='res-url'
              type='url'
              name='url'
              value={formData.url || ''}
              onChange={handleChange}
              placeholder='https://'
              required
            />
          </div>
          <div className='g-space-1'></div>
          <div className='field'>
            <label htmlFor='res-description'>Description</label>
            <textarea
              id='res-description'
              name='description'
              value={formData.description || ''}
              onChange={handleChange}
              placeholder='Brief description of this resource...'
              rows={3}
            />
          </div>
          <div className='g-space-1'></div>
          <div className='field'>
            <label>Tags</label>
            <div className='tags-input-area'>
              {formData.tags?.map((tag) => (
                <span key={tag} className='tag-chip'>
                  {tag}
                  <button
                    type='button'
                    className='tag-remove'
                    onClick={() => removeTag(tag)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              ))}
              <div className='tag-input-wrapper'>
                <input
                  type='text'
                  value={tagInput}
                  onChange={handleTagInputChange}
                  onKeyDown={handleTagKeyDown}
                  placeholder='Type a tag and press Enter...'
                  className='tag-text-input'
                />
                {tagInput.trim() && (
                  <button
                    type='button'
                    className='g-button small-btn'
                    onClick={() => addTag(tagInput)}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                )}
              </div>
            </div>
            {tagSuggestions.length > 0 && (
              <div className='tag-suggestions'>
                {tagSuggestions.map((tag) => (
                  <span
                    key={tag}
                    className='tag-suggestion'
                    onClick={() => addTag(tag)}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className='g-space-1'></div>
          <div className='form-buttons'>
            {resourceToEdit && (
              <button
                type='button'
                className='g-button med-btn'
                onClick={handleCancel}
              >
                Cancel
              </button>
            )}
            <button type='submit' className='g-button primary'>
              {resourceToEdit ? 'Update Resource' : 'Save Resource'}
            </button>
          </div>
        </form>
      </div>

      <div className='g-space-2'></div>

      <div className='g-card'>
        <div className='section-title'>
          Existing Resources
          <button
            className='g-button med-btn'
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived ? 'Hide Archived' : 'View Archived'}
          </button>
        </div>
        <hr />
        <SearchBar search={(val) => { setSearchQuery(val); setVisibleCount(10); }} idLabel='resource-search' />
        <div className='g-space-1'></div>
        {resources.length === 0 ? (
          <p className='meta'>No resources yet.</p>
        ) : (
          <div className='resources-list'>
            {(() => {
              const filtered = resources.filter((resource) => {
                const archivedMatch = showArchived
                  ? resource.status === 'archived'
                  : resource.status !== 'archived';
                if (!archivedMatch) return false;
                if (!searchQuery.trim()) return true;
                const q = searchQuery.toLowerCase();
                return (
                  resource.title?.toLowerCase().includes(q) ||
                  resource.description?.toLowerCase().includes(q) ||
                  resource.type?.toLowerCase().includes(q) ||
                  resource.tags?.some((t) => t.toLowerCase().includes(q))
                );
              });
              const visible = filtered.slice(0, visibleCount);
              return (
                <>
                  {
                  visible
                  .sort((a, b) => b.ts_created - a.ts_created)
                  .map((resource) => (
                <div
                  key={resource.id}
                  className={`resource-row${resource.status === 'hidden' ? ' hidden-resource' : ''}${resource.status === 'archived' ? ' archived-resource' : ''}`}
                >
                  <div className='resource-row-main'>
                    <div className='resource-info'>
                      <span className='resource-title'>{resource.title}</span>
                      <span className='resource-type meta'>{properCase(resource.type)} | Created {format(new Date().getTime(), "P")}</span>
                      <span className='resource-views meta'>
                        <FontAwesomeIcon icon={faEye} /> {resource.visitCount ?? 0} views
                      </span>
                      {resource.tags?.length > 0 && (
                        <div className='resource-tags'>
                          {resource.tags.map((tag) => (
                            <span key={tag} className='tag-chip small'>
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className='resource-actions'>
                      <button
                        type='button'
                        className='g-button small-btn'
                        onClick={() => loadForEdit(resource)}
                        title='Edit'
                      >
                        <FontAwesomeIcon icon={faPen} /> Edit
                      </button>
                      {resource.status === 'archived' ? (
                        <button
                          type='button'
                          className='g-button small-btn'
                          onClick={() => hideOrArchiveResource(resource, 'reinstate')}
                          title='Reinstate'
                        >
                          <FontAwesomeIcon icon={faEye} /> Reinstate
                        </button>
                      ) : (
                        <>
                          <button
                            type='button'
                            className='g-button small-btn'
                            onClick={() => hideOrArchiveResource(resource, resource.status === 'hidden' ? 'reinstate' : 'hide')}
                            title={resource.status === 'hidden' ? 'Show' : 'Hide'}
                          >
                            <FontAwesomeIcon icon={resource.status === 'hidden' ? faEye : faEyeSlash} />{' '}
                            {resource.status === 'hidden' ? 'Show' : 'Hide'}
                          </button>
                          <button
                            type='button'
                            className='g-button small-btn'
                            onClick={() => hideOrArchiveResource(resource, 'archive')}
                            title='Archive'
                          >
                            <FontAwesomeIcon icon={faBoxArchive} /> Archive
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {resource.description && (
                    <p className='resource-description meta'>
                      {resource.description}
                    </p>
                  )}
                  {resource.url && (
                    <a
                      href={resource.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='g-button small-btn resource-url-btn'
                    >
                      Visit Resource
                    </a>
                  )}
                </div>
              ))}
              {filtered.length > visibleCount && (
                <div className='load-more-container'>
                  <button
                    type='button'
                    className='g-button med-btn'
                    onClick={() => setVisibleCount((v) => v + 10)}
                  >
                    Load More
                  </button>
                </div>
              )}
                </>
              );
            })()}
          </div>
        )}
      </div>
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
    </div>
  );
};

export default ResourceManager;
